/**
 * Whether a FOLDER may be moved somewhere, and what to say when a folder
 * change fails. UI-side only: the server enforces every rule here on its own
 * (`PROTECTED_FOLDER`, `PROTECTED_FOLDER_PATH`, `FOLDER_MOVE_INTO_SELF`,
 * `FOLDER_MOVE_INTO_DESCENDANT`), so none of this is a security boundary.
 *
 * Files are placed by the placement controller (`DocumentPlacement.tsx`),
 * not here — folders have no visibility and no area of their own beyond the
 * folders they sit in.
 */
import { ApiError, type VaultDocumentFolderDto } from '@/lib/api';

/** True when `candidateId` is `ancestorId` itself or sits anywhere
 * below it. Walks `parentFolderId` pointers rather than comparing the
 * denormalized `path` strings, so the answer stays right even if a
 * path is momentarily stale between a move and its refetch. `seen`
 * guards against a cycle in the data — the server refuses to create
 * one, but an infinite loop in a dragover handler is not an
 * acceptable failure mode. */
export function isSelfOrDescendant(
  byId: ReadonlyMap<string, VaultDocumentFolderDto>,
  ancestorId: string,
  candidateId: string,
): boolean {
  const seen = new Set<string>();
  let cursor: string | null = candidateId;
  while (cursor && !seen.has(cursor)) {
    if (cursor === ancestorId) return true;
    seen.add(cursor);
    cursor = byId.get(cursor)?.parentFolderId ?? null;
  }
  return false;
}

/** Client-side mirror of the server's guards in
 * `moveVaultDocumentFolder`. This is a UX affordance only — it
 * suppresses the drop cursor and a pointless round trip. Protected
 * folders (the Arche Saves and My-Fias roots and every platform folder
 * under them) never move and never receive a folder. */
export function canMoveFolderInto(
  byId: ReadonlyMap<string, VaultDocumentFolderDto>,
  sourceFolderId: string,
  targetFolderId: string | null,
): boolean {
  const source = byId.get(sourceFolderId);
  // System folders are immovable, and a move to where it already
  // lives is a no-op we shouldn't dignify with a request.
  if (!source || source.isProtected) return false;
  if (targetFolderId === source.folderId) return false;
  if ((source.parentFolderId ?? null) === targetFolderId) return false;
  if (targetFolderId === null) return true;
  const target = byId.get(targetFolderId);
  // Anything protected is a system area — the resulting path would be
  // rejected by the ops layer.
  if (!target || target.isProtected) return false;
  return !isSelfOrDescendant(byId, source.folderId, targetFolderId);
}

/** Turn an op error into copy a user can act on. The folder routes
 * emit 409 `FOLDER_CONFLICT` for non-empty-on-delete, name-clash, and
 * a move into the folder's own subtree, and a delete whose contents changed
 * under it (`FOLDER_CHANGED`); 403 `FORBIDDEN` for protected
 * paths, cross-Holder writes, and a delete of a folder holding files above
 * the caller's tier. */
export function friendlyFolderError(
  err: unknown,
  action: 'create' | 'rename' | 'delete' | 'move',
): string {
  if (err instanceof ApiError) {
    if (err.status === 409) {
      // Both cycle and name-clash surface as FOLDER_CONFLICT; only the
      // ops-layer message tells them apart.
      if (err.message?.includes('FOLDER_MOVE_INTO_DESCENDANT')) {
        return 'A folder cannot be moved into one of its own subfolders.';
      }
      // A file inside changed (or went) while the folder was being deleted.
      if (err.message?.includes('FOLDER_CHANGED')) {
        return 'The folder changed while it was being deleted. Try again.';
      }
      return 'A folder with that name already exists at this location.';
    }
    if (err.status === 403) {
      // A folder holding a file above the caller's tier is refused whole.
      if (err.message?.includes('FOLDER_HAS_RESTRICTED_FILES')) {
        return 'This folder holds files you don’t have access to. Ask a workspace owner or admin to delete it.';
      }
      return action === 'move'
        ? 'System folders cannot be moved, and nothing can be moved into them.'
        : 'System folders cannot be modified.';
    }
    if (err.status === 400) {
      return (
        err.message || (action === 'move' ? 'That move is not allowed.' : 'Folder name is invalid.')
      );
    }
  }
  return err instanceof Error ? err.message : `Failed to ${action} folder.`;
}
